<template>
  <vbt-dialog
    v-model="dialog"
    :disable-submit-btn="isInvalidForm"
    title="Substrate replacement"
    submit-btn-name="Send"
    width="800px"
    @submit="sendToQueue"
    @close="resetForm"
  >
    <v-layout>
      Original substrate:
      <strong class="ml-1">
        {{ substrateName }}
      </strong>
    </v-layout>

    <v-layout>
      Press:
      <strong class="ml-1">
        {{ printMachine }}
      </strong>
    </v-layout>

    <v-layout class="mt-3">
      <v-flex sm4>
        <v-select
          v-model="form.name"
          :loading="substratesLoading"
          :items="substrates"
          label="Name"
          clearable
        />
      </v-flex>

      <v-flex
        class="px-5"
        sm4
      >
        <v-text-field
          v-model.trim.number="form.width"
          :rules="[validateDimension.bind({}, minWidth)]"
          :hint="`Min value: ${minWidth}`"
          label="Width (in)"
          type="number"
          clearable
          @keypress.enter="sendToQueue"
        />
      </v-flex>

      <v-flex sm4>
        <v-text-field
          v-model.trim.number="form.height"
          :rules="[validateDimension.bind({}, minHeight)]"
          :hint="`Min value: ${minHeight}`"
          label="Height (in)"
          type="number"
          clearable
          @keypress.enter="sendToQueue"
        />
      </v-flex>
    </v-layout>

    <span
      v-if="isInvalidForm"
      class="warning--text"
    >
      At least one field is required
    </span>
  </vbt-dialog>
</template>

<script>
import { mapState } from 'vuex';

import { wrapToLoadingFn } from '@helpers';

import { useDialog } from '@mixins/factories';

import { LOCATIONS_NAMESPACE } from '@store/modules/locations.types';

import { SubstratesApiService } from '@modules/additional/substrates';

import { DashboardApiService } from '../_services/dashboard.api.service';

const createSubstrateForm = () => ({ name: null, width: null, height: null });

const isEmptyForm = form => (
  !form.name && typeof form.width !== 'number' && typeof form.height !== 'number'
);

const prepareSubstrateReplacementByForm = form => ({
  height: typeof form.height === 'number' ? form.height : null,
  width: typeof form.width === 'number' ? form.width : null,
  name: form.name || null,
});

export default {
  name: 'PbDashboardToQueueSender',

  props: {
    // Dashboard filters
    // Should include: groupFilter, shippingPeriods
    filters: {
      type: Object,
      required: true,
    },

    sentItem: {
      type: Object,
      required: true,
    },

    printMachine: {
      type: String,
      required: true,
    },

    closeLoadingSpinnerAfterSuccess: {
      type: Boolean,
      default: true,
    },

    substrateName: {
      type: String,
      required: true,
    },

    minWidth: {
      type: Number,
      required: true,
    },

    minHeight: {
      type: Number,
      required: true,
    },
  },

  mixins: [
    useDialog(),
  ],

  data() {
    return {
      form: createSubstrateForm(),

      substratesLoading: false,
      substrates: [],
    };
  },

  computed: {
    ...mapState(LOCATIONS_NAMESPACE, ['suffix']),

    isInvalidForm() {
      return isEmptyForm(this.form)
        || (typeof this.form.width === 'number' && this.form.width < this.minWidth)
        || (typeof this.form.height === 'number' && this.form.height < this.minHeight);
    },
  },

  watch: {
    suffix: {
      handler(newValue) {
        if (newValue) {
          this.getSubstrates(newValue);
        }
      },
      immediate: true,
    },
  },

  methods: {
    sendToQueue() {
      if (this.isInvalidForm) {
        return;
      }

      if (!this.closeLoadingSpinnerAfterSuccess) {
        this.$VBlackerTheme.openLoadingSpinner();
      }

      this.wrapToLoadingFn({
        req: DashboardApiService.sendGroupItemToMachine.bind({}, {
          substrateReplacement: prepareSubstrateReplacementByForm(this.form),
          shippingPeriods: this.filters.shippingPeriods,
          groupFilter: this.filters.groupFilter,

          fixedJobs: this.sentItem.fixedJobs || [],
          operation: this.sentItem.operation,
          substrate: this.sentItem.substrate,
          height: this.sentItem.height,
          width: this.sentItem.width,

          machine: this.printMachine,
          suffix: this.suffix,
        }),
        fullscreenLoadingSpinner: this.closeLoadingSpinnerAfterSuccess,
        closeLoadingSpinnerOnError: !this.closeLoadingSpinnerAfterSuccess,
        onSuccess: () => {
          this.$emit('success:sent');
          this.dialog = false;
        },
      });
    },

    getSubstrates(suffix) {
      this.wrapToLoadingFn({
        req: SubstratesApiService.getAllSubstrates.bind({}, { suffix }),
        loadingFlagName: 'substratesLoading',
        onSuccess: (r) => {
          this.substrates = r.map(({ name }) => name);
        },
      });
    },

    validateDimension(minValue, value) {
      return (typeof value !== 'number') || (value >= minValue) || `Should be greater or equal ${minValue}`;
    },

    resetForm() {
      Object.assign(this.form, createSubstrateForm());
    },

    wrapToLoadingFn,
  },
};
</script>
